import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	type ReactNode,
} from "react"

const LOCAL_WISHLIST_KEY = "fenix_wishlist"
const WISHLIST_MERGED_KEY = "fenix_wishlist_merged"
const WISHLIST_UPDATE_EVENT = "fenix_wishlist_update"

type WishlistContextType = {
	wishlist: string[]
	addToWishlist: (productId: string) => void
	removeFromWishlist: (productId: string) => void
	toggleWishlist: (productId: string) => void
	isInWishlist: (productId: string) => boolean
	clearWishlist: () => void
	isServerWishlist: boolean
}

const WishlistContext = createContext<WishlistContextType | undefined>(
	undefined,
)

export function WishlistProvider({
	children,
	serverWishlistIds = [],
}: {
	children: ReactNode
	serverWishlistIds?: string[]
}) {
	const [wishlist, setWishlist] = useState<string[]>(serverWishlistIds)
	const [isInitialized, setIsInitialized] = useState(false)

	useEffect(() => {
		if (serverWishlistIds.length > 0) {
			setWishlist(serverWishlistIds)
			setIsInitialized(true)
			return
		}

		const stored = localStorage.getItem(LOCAL_WISHLIST_KEY)
		if (stored) {
			try {
				const parsed = JSON.parse(stored)
				setWishlist(Array.isArray(parsed) ? (parsed as string[]) : [])
			} catch {
				setWishlist([])
			}
		}
		setIsInitialized(true)
	}, [serverWishlistIds])

	useEffect(() => {
		const handleStorageChange = () => {
			const stored = localStorage.getItem(LOCAL_WISHLIST_KEY)
			if (stored) {
				try {
					const parsed = JSON.parse(stored)
					setWishlist(Array.isArray(parsed) ? (parsed as string[]) : [])
				} catch {
					setWishlist([])
				}
			} else {
				setWishlist(serverWishlistIds.length > 0 ? serverWishlistIds : [])
			}
		}

		window.addEventListener(WISHLIST_UPDATE_EVENT, handleStorageChange)
		window.addEventListener("storage", handleStorageChange)

		return () => {
			window.removeEventListener(WISHLIST_UPDATE_EVENT, handleStorageChange)
			window.removeEventListener("storage", handleStorageChange)
		}
	}, [serverWishlistIds])

	const persistWishlist = useCallback(
		(items: string[]) => {
			localStorage.setItem(LOCAL_WISHLIST_KEY, JSON.stringify(items))
			window.dispatchEvent(new CustomEvent(WISHLIST_UPDATE_EVENT))
		},
		[serverWishlistIds.length === 0],
	)

	const addToWishlist = useCallback(
		(productId: string) => {
			const newWishlist = [...wishlist, productId]
			setWishlist(newWishlist)
			if (serverWishlistIds.length === 0) {
				persistWishlist(newWishlist)
			}
		},
		[wishlist, serverWishlistIds.length, persistWishlist],
	)

	const removeFromWishlist = useCallback(
		(productId: string) => {
			const newWishlist = wishlist.filter(id => id !== productId)
			setWishlist(newWishlist)
			if (serverWishlistIds.length === 0) {
				persistWishlist(newWishlist)
			}
		},
		[wishlist, serverWishlistIds.length, persistWishlist],
	)

	const toggleWishlist = useCallback(
		(productId: string) => {
			if (wishlist.includes(productId)) {
				removeFromWishlist(productId)
			} else {
				addToWishlist(productId)
			}
		},
		[wishlist, addToWishlist, removeFromWishlist],
	)

	const isInWishlist = useCallback(
		(productId: string) => wishlist.includes(productId),
		[wishlist],
	)

	const clearWishlist = useCallback(() => {
		setWishlist([])
		if (serverWishlistIds.length === 0) {
			persistWishlist([])
		}
	}, [serverWishlistIds.length, persistWishlist])

	return (
		<WishlistContext.Provider
			value={{
				wishlist: isInitialized ? wishlist : [],
				addToWishlist,
				removeFromWishlist,
				toggleWishlist,
				isInWishlist,
				clearWishlist,
				isServerWishlist: serverWishlistIds.length > 0,
			}}
		>
			{children}
		</WishlistContext.Provider>
	)
}

export function useWishlist() {
	const context = useContext(WishlistContext)
	if (!context) {
		throw new Error("useWishlist must be used within a WishlistProvider")
	}
	return context
}

export function getLocalWishlist(): string[] {
	if (typeof window === "undefined") return []
	const stored = localStorage.getItem(LOCAL_WISHLIST_KEY)
	if (!stored) return []
	try {
		const parsed = JSON.parse(stored)
		return Array.isArray(parsed) ? (parsed as string[]) : []
	} catch {
		return []
	}
}
